import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutlet } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";

import Breadcrumbs from "components/Common/BreadcrumbTitle";
import { getMeetingByEntity, getMeetingByID, getTaskById } from "../../http/fundLync/entityAndAddress.helper";
import { decryptedValue, isPermission } from "common/utility";
import TableContainerV1 from "components/Common/TableContainerV1";

const isBase64 = (str) => {
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
};

const TableActionsComponent = ({ rowData, onEditMeeting, onViewMeeting }) => {
  const user = decryptedValue(localStorage.getItem("user"));

  return (
    <UncontrolledDropdown>
      <DropdownToggle tag="a" className="card-drop">
        <i className="mdi mdi-dots-horizontal font-size-18"></i>
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-end">
        <DropdownItem onClick={() => onEditMeeting(rowData.id)}>
          <i
            className="mdi mdi-pencil font-size-16 text-success me-1"
            id="edittooltip"
          ></i>
          Edit
          <UncontrolledTooltip placement="top" target="edittooltip">
            Edit
          </UncontrolledTooltip>
        </DropdownItem>

        <DropdownItem onClick={() => onViewMeeting(rowData.id)}>
          <i
            className="mdi mdi-eye font-size-16 text-success me-1"
            id="viewTooltip"
          ></i>
          View
          <UncontrolledTooltip placement="top" target="viewTooltip">
            View
          </UncontrolledTooltip>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const EntityMeetingTable = ({ onCreateNewMeeting, onEditMeeting, onViewMeeting }) => {
  document.title = "Meeting Table | Fundlync";

  const navigate = useNavigate();
  const [meetingList, setMeetingList] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  });
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [columnId, setColumnId] = useState("id");
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
  const [isNewEntityPermission, setIsNewEntityPermission] = useState(false);
  // const userData = localStorage.getItem("user") || "{}";
  const userObj = decryptedValue(localStorage.getItem("user"));
  const { entityType } = userObj || "";
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action"],
  });
  const [entityId, setEntityId] = useState();

  const urlParams = new URLSearchParams(window.location.search);
  const filterType = urlParams.get("filter");
  let decodeFilterType = isBase64(filterType) ? atob(filterType) : filterType;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedId = urlParams.get("id");
    if (encodedId) {
      const decodedId = isBase64(encodedId) ? atob(encodedId) : encodedId;
      setEntityId(decodedId);
    } else {
      const entity = decryptedValue(localStorage.getItem("user"));
      setEntityId(entity.entityId);
    }
  }, []);
  const navigateToDeal = async (Id) => {
    try {
      const dealInfoResponse = await getMeetingByID({ Id });
      const entityId = dealInfoResponse?.dealId;
      try {
        const encryptedId = btoa(entityId.toString());
        if(userObj.roleName === "fundLync_cutomer"){
          navigate(`/customer/viewDeal?id=${encodeURIComponent(encryptedId)}`);
        }else{
          navigate(`/${userObj.roleName}/viewDeal?id=${encodeURIComponent(encryptedId)}`);
        }
        
      } catch (entityError) {
        console.error('Error fetching entity details:', entityError);
      }
    } catch (dealError) {
      console.log('Error fetching entity details:', dealError)
    }
  };

  const handleMouseEnter = (event) => {
    event.target.style.color = "#34c38f";
    event.target.style.textDecoration = 'underline';
  };
  const handleMouseLeave = (event) => {
    event.target.style.color = "#21aa47";
    event.target.style.textDecoration = 'none';
  };
  const statusColors = {
    "Scheduled": "#3a99db", // blue
    "Conducted": "#34c38f", // green
    "Missed": "#ffc107", // yellow
    "Cancelled": "#f46a6a", // red
  };
  const truncateText = (text, charLimit) => {
    if (text && text.length > charLimit) {
      const truncated = text.substring(0, charLimit) + "...";
      return truncated;
    }
    return text;
  };
  const entityDetails = decryptedValue(localStorage.getItem("user"));
  let handleChangeDeal;
  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
        filterable: true,
        Cell: (cellProps) => {
          handleChangeDeal = () => {
            navigateToDeal(cellProps.value)
          }
          return cellProps.value || "";
        },
      },
      {
        Header: "Deal Name",
        accessor: "dealId",
        filterable: true,
        Cell: (cellProps) => {
          let rowData = cellProps?.row?.original?.deal
          return (
            <div
            onClick={handleChangeDeal}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: 'pointer', color: '#21aa47', width: '160px' }}
            title={rowData?.dealName || ''}
          >
            <span>{truncateText(rowData?.dealName, 15) || ""}</span>
          </div>
          );
        }
      },
      {
        Header: "Meeting Title",
        accessor: "title",
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value || "";
        },
      },
      {
        Header: "Meeting Date",
        accessor: "date",
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value || "";
        },
      },
      {
        Header: "Start Time",
        accessor: "startTime",
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value || "";
        },
      },
      {
        Header: "Meeting Status",
        accessor: "meetingStatus",
        filterable: true,
        Cell: (cellProps) => {
          let rowData = cellProps?.row?.original
          const status = cellProps.value || "";
          const color = statusColors[status] || "#6c757d";
          return (
            <span onClick={()=>{handleStatusChange(rowData)}}
              // onMouseEnter={handleMouseEnter}
              // onMouseLeave={handleMouseLeave}
              style={{
                cursor: 'pointer',
                color: color, 
                width: '110px',
                fontWeight: '600',
                padding: '5px 15px', 
                borderRadius: '20px',
                border: `2px solid ${color}`,
                display: 'inline-block', 
                textAlign: 'center', 
                lineHeight: 'normal',
              }}
            >
              {rowData?.meetingStatus || ''}
            </span>
          );
        },
      },
      {
        Header: "Location",
        accessor: "location",
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value || "";
        },
      },
      {
        Header: "Action",
        Cell: (cellProps) => {
          const rowData = cellProps.row.original;
          return (
            <TableActionsComponent
              rowData={rowData}
              onEditMeeting={onEditMeeting}
              onViewMeeting={onViewMeeting}
            />
          );
        },
      },
    ],
    []
  );

  const convertTo12HourFormat = (time) => {
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours, 10);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert to 12-hour format
    return `${hours}:${minutes} ${ampm}`;
  };

  const getMeetingList = (data, entityId) => {
    const requestData = { ...data, entityId };

    getMeetingByEntity(requestData)
      .then((responseFromAPI) => {
        if (responseFromAPI.status && responseFromAPI.code === 200) {
          const formattedMeetings = responseFromAPI.data.data.map(meeting => {
            return {
              ...meeting,
              date: formatDate(meeting.date),
              startTime: convertTo12HourFormat(meeting.startTime),
              endTime: convertTo12HourFormat(meeting.endTime)
            };
          });
          setMeetingList(formattedMeetings);
          const { totalItems, totalPages, currentPage } = responseFromAPI.data;
          setPagination({
            pageIndex: currentPage,
            pageCount: totalPages,
            totalRecords: totalItems,
          });
        } else {
          setMeetingList([]);
          setPagination({
            pageIndex: 0,
            pageCount: 0,
            totalRecords: 0,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching meetings", error);
      });
  };

  const handlePageChange = (
    page,
    pageSize,
    searchKey=searchValue,
    columnName = columnId,
    sortingOrder = sortingOrderStatus
  ) => {
    setPageSize(pageSize);
    const data = {
      page,
      limit: pageSize,
      searchKey,
      columnName,
      sortingOrder,
    };
    if (filterType) {
      data.filterType = decodeFilterType;
    }
    getMeetingList(data, entityId);
  };

  const handleSortColumn = (option) => {
    setSortingOptions(option);
    const columnName = option.column;
    setColumnId(columnName);
    const sortingOrder = option.isAsc ? "asc" : "desc";
    setSortingOrderStatus(sortingOrder);
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder);
  };

  const handleSearchFilter = (value, pageSize) => {
    if (filterType) {
      if (value) {
        setSearchValue(value);
        handlePageChange(1, pageSize, value);
      } else {
        setSearchValue(decodeFilterType);
        handlePageChange(1, pageSize, decodeFilterType);
      }
    } else {
      setSearchValue(value);
      handlePageChange(1, pageSize, value);
    }
  };

  const handleClearSearch = () => {
    if (filterType) {
      setSearchValue(decodeFilterType);
      handlePageChange(1, pageSize, decodeFilterType);
    } else {
      setSearchValue("");
      handlePageChange(1, pageSize, "");
    }
  };

  useEffect(() => {
    if (entityId) {
      if (filterType) {
        getMeetingList({ searchKey: `${decodeFilterType}` }, entityId);
      } else {
        getMeetingList({}, entityId);
      }
    }
  }, [entityId, filterType]);

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const isNewEntitypermission = await isPermission(
      "apiConfiguration",
      "create_permission"
    );
    setIsNewEntityPermission(isNewEntitypermission);
  };

  // Format date
  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  const buttonSection = (
    <div className="text-sm-end">
      <Button
        type="button"
        style={{ backgroundColor: '#21aa47', borderColor: '#21aa47' }}
        className="mb-2 me-2"
        onClick={onCreateNewMeeting}
      >
        <i className="mdi mdi-plus me-1" />
        {"Create Meeting"}
      </Button>
    </div>
  );

  return (
    <React.Fragment>
      <Col xs="12">
        <TableContainerV1
          columns={columns}
          data={meetingList}
          pagination={pagination}
          handlePageChange={handlePageChange}
          handleSearchFilter={handleSearchFilter}
          sortOptions={sortingOptions}
          customSection={buttonSection}
          handleSortColumn={handleSortColumn}
          handleClearSearch={handleClearSearch}
          className="custom-header-css"
        />
      </Col>
    </React.Fragment>
  );
};

export default EntityMeetingTable;
